import { useEffect, useMemo, useRef, useState } from 'react';

import {
  CAVEAT_MANUAL,
  CAVEAT_PARTY_FROM_PREFIX,
  CAVEAT_UNSIGNED,
  type ApprovedIngestItem,
  type ExecuteSharepointIngestResponse,
  type IngestedDocument,
  type IngestSkipReason,
  type PlannedDocument,
  type SkippedDocument,
} from '@clm/shared-types';

import { ContractTypeTag } from '../../components/ContractTypeTag';
import { SelectionToolbar } from '../../components/SelectionToolbar';
import { TagInput } from '../../components/TagInput';
import { BaseDeviationOffer } from '../BaseDeviationOffer';
import { AMENDMENT_SIGNAL_COPY } from '../type-proposal-copy';
import { rowKey, type RowOverride } from './planner-store';
import { SOW_SHAPE_LABEL } from './sow-shape';

/**
 * Approve-and-execute for the Ingest planner — CLM-67 (D-67-1, D-67-2).
 *
 * The plan's `to_ingest` rows become a ticked list. Every row has a checkbox;
 * a row is pre-ticked **iff** `signed_marker === true`. A row planned without
 * the signed marker (only possible under `include_unsigned_pdfs`) is never
 * pre-ticked and carries the visible "no signed marker" tag — that is the
 * control against ingesting a draft: it must never happen by default. D-67-2 as
 * amended 11 Sep 2026 (Gerry): a group's "Select all" is an explicit human act
 * and ticks **every** candidate row of the group, signed or not — in the real
 * Global PMO library almost no executed SOW carries the word "signed" in its
 * filename, so a select-all limited to signed rows visibly did nothing. The
 * inline confirmation then says how many of the ticked rows are unsigned.
 * The controls are the shared `SelectionToolbar` (CLM-69): this file keeps the
 * D-67 semantics, the component keeps the vocabulary — and the top-level bar
 * also carries a Select all / Clear across every group (D-69-3).
 *
 * What leaves the browser on Execute is exactly what CLM-50 designed: the
 * folder link and the `(drive_id, item_id, etag)` triples of the ticked rows in
 * plan order, plus the plan's own `max_documents` / `include_unsigned_pdfs` when
 * it had them (D-67-3 — the re-crawl must see the same candidates). No
 * `party_key`, no `sow_number`, no `plan_id`: the server re-plans and executes
 * only the triples the fresh plan still wants.
 *
 * The receipt renders what the server returned and nothing else. The surface
 * never marks a row ingested itself — "Re-plan this folder" re-posts the same
 * plan request, and the ingested rows come back under "Already ingested".
 *
 * D-67-6 — neither card owns state. The ticks and the open confirm prompt are
 * read from `planner-store.ts` and written back through the callbacks, so they
 * survive the surface being unmounted by a NavRail switch and are re-derived
 * by the store when a new plan arrives (the parent no longer remounts this
 * card per plan).
 *
 * CLM-118 (D-110-3, R-118-18) — nobody picks a type here. Each row shows the
 * planner's type, from the file or folder name. The receipt has no override
 * column for the type.
 *
 * CLM-122 (D-110-4 point 4) — a row whose name gives no type (`no_name_type`) is
 * offered as **Type pending**: filed with no type for the AI to read after
 * filing. It is never pre-ticked and sits in its own "No type in the name" group,
 * which no Select all reaches — the top-level one included — because a purchase
 * order or a timesheet is exactly a file whose name gives no type: the reviewer
 * ticks each one and confirms only its client (R-122-4, R-122-5). A row whose
 * name contradicts itself (`conflicting_names`) is Type pending too, never pre-
 * ticked, but stays in its client's group, whose Select all reaches it. Both can
 * be skipped (R-122-6). A Select all that cannot reach every row stays inert
 * when it has nothing left to tick (R-122-15); Clear clears every row.
 */

/** CLM-118 (R-118-6), CLM-122 (R-122-3) — the caveat on a planned row whose name contradicts itself. */
export const CONFLICTING_NAME_CAVEAT =
  'The name names more than one type, or a type and a SOW number, so it is filed as Type pending: the AI reads the document after filing and decides the type.';

/** CLM-122 (R-122-14) — what a row whose name gives no type is, and what ticking it does. */
export const TYPE_PENDING_COPY =
  'No type in the file or folder name. Tick it only if it is a contract: it is filed as Type pending, and the AI reads the document after filing and decides the type. If the AI reads a SOW, an administrator adds its number.';

/** CLM-122 — added for a processing-restricted Type pending row (rule 4). */
export const TYPE_PENDING_RESTRICTED_COPY = 'Processing-restricted: never sent to the AI — an administrator types it.';

/** Compact skip copy for the receipt; the plan's own card carries the long form. */
const SKIP_REASON_LABEL: Readonly<Record<IngestSkipReason, string>> = {
  party_key_not_derivable: 'Party key not derivable',
  sow_number_unusable: 'SOW number unusable',
  sow_number_not_matched: 'SOW number not matched',
  sow_number_ambiguous: 'SOW number ambiguous',
  already_ingested: 'Already ingested',
  no_etag: 'No eTag from SharePoint',
  /* CLM-88 (D-88-2). */
  party_ambiguous: 'Client is ambiguous',
};

const FAILURE_REASON_LABEL: Readonly<
  Record<ExecuteSharepointIngestResponse['failed'][number]['reason'], string>
> = {
  download_failed: 'Download failed',
  version_changed: 'Version changed during execute',
  document_unusable: 'Document unusable',
  write_failed: 'Write failed',
  /* CLM-71 (D-71-6): the admin's correction was refused — the detail names the rule. */
  override_rejected: 'Correction refused',
  /*
   * CLM-110 (R-110-10): the minted key is already held — by a contract outside your scope, or a
   * writer that skipped the lock. Not filed, and never "Already ingested": the detail names it.
   */
  key_collision: 'Key already held — not filed',
  /* CLM-110 (R-110-10): the client's four-digit number series is used up; never truncated. */
  key_sequence_exhausted: 'Number series used up',
};

/** CLM-71 — a skipped row the admin corrected, as the ingest card lists it. */
export interface CorrectedRow {
  readonly row: SkippedDocument;
  readonly override: RowOverride;
}

export interface IngestExecuteProps {
  readonly documents: readonly PlannedDocument[];
  /** Disabled while the parent is planning or executing. */
  readonly busy: boolean;
  /** Row keys (`rowKey`) ticked on this plan — from the store. */
  readonly selected: ReadonlySet<string>;
  /** The inline confirmation is open — from the store. */
  readonly confirming: boolean;
  /** Tick (`on`) or clear the rows named by key — one row, or a group's rows. */
  readonly onSelect: (keys: readonly string[], on: boolean) => void;
  readonly onConfirm: (open: boolean) => void;
  /**
   * Post the ticked rows. The parent builds the triples from the store's
   * selection in plan order (D-67-1) and flips the phase to `executing`.
   */
  readonly onExecute: () => void;
  /**
   * CLM-71 — the `to_skip` rows the admin corrected, listed under "Corrected by
   * hand". Never pre-ticked; ticked ones travel in `approved[]` with their
   * override fields. Optional so the card renders unchanged without them.
   */
  readonly corrected?: readonly CorrectedRow[];
  /** "Remove correction": the row returns to the skip card and is un-ticked. */
  readonly onRemoveCorrection?: (key: string) => void;
  /** CLM-77 — the tags held for a row (`rowKey`), `[]` when none. */
  readonly tagsOf?: (key: string) => readonly string[];
  /** CLM-77 — set one row's tags (the replacement set for that row). */
  readonly onSetTags?: (key: string, tags: readonly string[]) => void;
  /** CLM-122 (R-122-6) — Skip a Type pending row (CLM-115); omitted, no Skip is offered. */
  readonly onSkipTypePending?: (doc: PlannedDocument) => void;
}

/** CLM-122 — a planned row no Select all reaches: its name gives no type (R-122-4). */
const isNoNameType = (doc: PlannedDocument): boolean => doc.type_pending_reason === 'no_name_type';
/** CLM-122 — a corrected row that files as `no_name_type`, out of every Select all too. */
const isNoNameTypeCorrection = (entry: CorrectedRow): boolean =>
  entry.override.display.type_pending_reason === 'no_name_type';

/** Rows grouped by `party_key` in plan order — first appearance decides the order. */
function groupByParty(
  documents: readonly PlannedDocument[],
): readonly { readonly party_key: string; readonly rows: readonly PlannedDocument[] }[] {
  const order: string[] = [];
  const byParty = new Map<string, PlannedDocument[]>();
  for (const doc of documents) {
    const bucket = byParty.get(doc.party_key);
    if (bucket === undefined) {
      order.push(doc.party_key);
      byParty.set(doc.party_key, [doc]);
    } else bucket.push(doc);
  }
  return order.map((party_key) => ({ party_key, rows: byParty.get(party_key) ?? [] }));
}

/* -------------------------------------------------------------------------- */
/* To ingest — tick, select, execute                                           */
/* -------------------------------------------------------------------------- */

export function ToIngestCard({
  documents,
  busy,
  selected,
  confirming,
  onSelect,
  onConfirm,
  onExecute,
  corrected = [],
  onRemoveCorrection,
  tagsOf = () => [],
  onSetTags,
  onSkipTypePending,
}: IngestExecuteProps) {
  /* CLM-77 — the toolbar's "Apply tags to selected…" popover; appends, never replaces. */
  const [tagging, setTagging] = useState(false);
  const [bulkTags, setBulkTags] = useState<readonly string[]>([]);
  /*
   * D-67-2 — the default tick is the signed marker. It is derived by the store
   * when the plan arrives (`setPlan`), not here, so it survives an unmount and
   * is re-derived on Re-plan without this card being remounted.
   */
  const yesRef = useRef<HTMLButtonElement | null>(null);

  useEffect(() => {
    if (confirming) yesRef.current?.focus();
  }, [confirming]);

  /* CLM-122 (R-122-4) — a row whose name gives no type leaves the party groups for its own group. */
  const noNameType = useMemo(() => documents.filter(isNoNameType), [documents]);
  const groups = useMemo(() => groupByParty(documents.filter((doc) => !isNoNameType(doc))), [documents]);
  const selectedDocs = documents.filter((doc) => selected.has(rowKey(doc)));
  /** CLM-71 — corrected rows count like any other candidate; they are never pre-ticked. */
  const selectedCorrected = corrected.filter((entry) => selected.has(rowKey(entry.row)));
  /** Every row: what the counter counts and Clear clears (R-122-15) — Type pending included. */
  const allKeys = [...documents.map(rowKey), ...corrected.map((entry) => rowKey(entry.row))];
  /** CLM-122 (R-122-4) — what a Select all may tick: never a row whose name gives no type. */
  const selectAllKeys = [
    ...documents.filter((doc) => !isNoNameType(doc)).map(rowKey),
    ...corrected.filter((entry) => !isNoNameTypeCorrection(entry)).map((entry) => rowKey(entry.row)),
  ];
  const nothingLeftToSelect = selectAllKeys.every((key) => selected.has(key));
  const totalCount = documents.length + corrected.length;
  const selectedCount = selectedDocs.length + selectedCorrected.length;
  /** CLM-122 — named in the confirmation so a Type pending tick is approved knowingly. */
  const typePendingSelectedCount =
    selectedDocs.filter((doc) => doc.type_pending_reason !== null).length +
    selectedCorrected.filter((entry) => entry.override.display.type_pending_reason !== undefined).length;
  /**
   * Named in the confirmation so an unsigned tick is approved knowingly (D-67-2
   * amended). Planned rows only: a skipped row carries no `signed_marker`, and
   * the browser does not re-derive the walk filter's rule from the file name —
   * the server composes the receipt's tag from the fresh scan, and the
   * corrected-by-hand sentence says so.
   */
  const unsignedSelectedCount = selectedDocs.filter((doc) => !doc.signed_marker).length;
  const disabled = busy;

  function toggle(doc: PlannedDocument, checked: boolean) {
    onSelect([rowKey(doc)], checked);
  }

  /**
   * Select-all ticks every row of the group, unsigned included (D-67-2 as amended
   * 11 Sep 2026 — the click is the explicit human act; the load-time pre-tick
   * stays signed-only). Clear clears every row.
   */
  function selectGroup(rows: readonly PlannedDocument[], on: boolean) {
    onSelect(rows.map(rowKey), on);
  }

  /** CLM-77 — append the popover's tags to every ticked row, deduplicated; rows keep what they had. */
  function applyBulkTags() {
    if (onSetTags === undefined || bulkTags.length === 0) return;
    const keys = [...selectedDocs.map(rowKey), ...selectedCorrected.map((entry) => rowKey(entry.row))];
    for (const key of keys) onSetTags(key, [...new Set([...tagsOf(key), ...bulkTags])]);
    setBulkTags([]);
    setTagging(false);
  }

  return (
    <div className="card">
      <h3>To ingest — {totalCount.toLocaleString()}</h3>
      {totalCount === 0 ? (
        <p>
          Nothing qualifies: every matched document was skipped. The groups below say why, and most
          reasons name something fixable.
        </p>
      ) : (
        <>
          <p>
            Each row is a document the backfill would take: the file, the SOW number it matched and
            how, the party it resolved to, and what the pipeline will do with it. A row tagged{' '}
            <em>unregistered SOW number</em> carries a number CLM has not seen yet — it is still
            approvable, and corpus reconciliation checks the number against Qfly afterwards. A row
            tagged <em>no signed marker</em> was planned only because you asked for PDFs without the
            word — it is never pre-ticked; tick it, or take it in with the group's Select all, only
            if you know it is the executed document. A file whose name gives no type is listed under{' '}
            <em>No type in the name</em>, never pre-ticked and out of every Select all: tick each one
            you know is a contract, and it is filed as Type pending for the AI to read.
          </p>

          <SelectionToolbar
            label="Approve and execute"
            noun="documents"
            count={selectedCount}
            total={totalCount}
            disabled={disabled}
            selectAllDisabled={nothingLeftToSelect}
            onSelectAll={() => onSelect(selectAllKeys, true)}
            onClear={() => onSelect(allKeys, false)}
          >
            {confirming ? (
              <span className="confirm">
                Ingest {selectedCount.toLocaleString()}{' '}
                {selectedCount === 1 ? 'document' : 'documents'} under the versions shown?{' '}
                {unsignedSelectedCount > 0 ? (
                  <>
                    {unsignedSelectedCount.toLocaleString()} of the {selectedCount.toLocaleString()}{' '}
                    selected carry no signed marker — approve only if you know these are the
                    executed documents.{' '}
                  </>
                ) : null}
                {selectedCorrected.length > 0 ? (
                  <>
                    {selectedCorrected.length.toLocaleString()} corrected by hand — the server checks each
                    correction again, including whether the file carries a signed marker.{' '}
                  </>
                ) : null}
                {typePendingSelectedCount > 0 ? (
                  <>
                    {typePendingSelectedCount.toLocaleString()} of the {selectedCount.toLocaleString()} selected
                    are Type pending — filed with no type, for the AI to read.{' '}
                  </>
                ) : null}
                <button
                  ref={yesRef}
                  type="button"
                  className="btn sm pri"
                  disabled={disabled}
                  onClick={onExecute}
                >
                  Yes, execute {selectedCount.toLocaleString()}
                </button>{' '}
                <button
                  type="button"
                  className="btn sm qt"
                  disabled={disabled}
                  onClick={() => onConfirm(false)}
                >
                  Cancel
                </button>
              </span>
            ) : (
              <>
                {onSetTags === undefined ? null : (
                  <button
                    type="button"
                    className="btn sm"
                    disabled={disabled || selectedCount === 0}
                    aria-expanded={tagging}
                    aria-controls="ingest-bulk-tags"
                    onClick={() => setTagging((open) => !open)}
                  >
                    Apply tags to selected…
                  </button>
                )}
                <button
                  type="button"
                  className="btn pri"
                  disabled={disabled || selectedCount === 0}
                  onClick={() => onConfirm(true)}
                >
                  {busy
                    ? 'Executing…'
                    : `Execute ${selectedCount.toLocaleString()} ${selectedCount === 1 ? 'document' : 'documents'}`}
                </button>
              </>
            )}
          </SelectionToolbar>

          {tagging && onSetTags !== undefined ? (
            <div id="ingest-bulk-tags" className="card secondary tags-popover" role="group" aria-label="Apply tags to selected">
              <p className="mut">
                These tags are added to the {selectedCount.toLocaleString()} ticked{' '}
                {selectedCount === 1 ? 'document' : 'documents'} — appended to any a row already
                carries, never replacing them. They are stored with the contract in the same
                transaction, with one audit row per contract.
              </p>
              <TagInput id="ingest-bulk-tag-input" label="Tags to apply" value={bulkTags} onChange={setBulkTags} disabled={disabled} />
              <span className="fl">
                <button type="button" className="btn sm pri" disabled={disabled || bulkTags.length === 0 || selectedCount === 0} onClick={applyBulkTags}>
                  Apply to {selectedCount.toLocaleString()} selected
                </button>
                <button
                  type="button"
                  className="btn sm qt"
                  disabled={disabled}
                  onClick={() => {
                    setBulkTags([]);
                    setTagging(false);
                  }}
                >
                  Cancel
                </button>
              </span>
            </div>
          ) : null}

          {/* CLM-110 (R-110-23), CLM-118, CLM-122 — the planner's type, from the name; nobody picks one here (B9). */}
          <p className="mut" data-testid="ingest-type-provisional">
            Each type here comes from the file or folder name and is provisional: the AI reads every document after
            it is filed, and the type is confirmed when the file name and the AI agree — otherwise an administrator
            decides, unless auto-approval for AI-decided types approves an AI-decided MSA or NDA whose name gave no
            type. A row whose name gives no type, or contradicts itself, is filed as Type pending.
          </p>
          <div className="tblwrap">
            <table className="tbl">
              <thead>
                <tr>
                  <th scope="col" className="tick">
                    <span className="vh">Approve</span>
                  </th>
                  <th scope="col">File</th>
                  <th scope="col">Type</th>
                  <th scope="col">SOW number</th>
                  <th scope="col">Party</th>
                  <th scope="col">Pipeline</th>
                  <th scope="col">Tags</th>
                </tr>
              </thead>
              <tbody>
                {groups.map(({ party_key, rows }) => (
                  <GroupRows
                    key={party_key}
                    party_key={party_key}
                    rows={rows}
                    selected={selected}
                    disabled={disabled}
                    onToggle={toggle}
                    onSelectGroup={selectGroup}
                    tagsOf={tagsOf}
                    onSetTags={onSetTags}
                    onSkip={onSkipTypePending}
                  />
                ))}
                {noNameType.length > 0 ? (
                  <NoNameTypeGroup
                    rows={noNameType}
                    selected={selected}
                    disabled={disabled}
                    onToggle={toggle}
                    onSelect={onSelect}
                    tagsOf={tagsOf}
                    onSetTags={onSetTags}
                    onSkip={onSkipTypePending}
                  />
                ) : null}
                {corrected.length > 0 ? (
                  <CorrectedGroup
                    entries={corrected}
                    selected={selected}
                    disabled={disabled}
                    onSelect={onSelect}
                    onRemove={onRemoveCorrection}
                    tagsOf={tagsOf}
                    onSetTags={onSetTags}
                  />
                ) : null}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}

function GroupRows({
  party_key,
  rows,
  selected,
  disabled,
  onToggle,
  onSelectGroup,
  tagsOf,
  onSetTags,
  onSkip,
}: {
  readonly party_key: string;
  readonly rows: readonly PlannedDocument[];
  readonly selected: ReadonlySet<string>;
  readonly disabled: boolean;
  readonly onToggle: (doc: PlannedDocument, checked: boolean) => void;
  readonly onSelectGroup: (rows: readonly PlannedDocument[], on: boolean) => void;
  readonly tagsOf: (key: string) => readonly string[];
  readonly onSetTags: ((key: string, tags: readonly string[]) => void) | undefined;
  /** CLM-122 (R-122-6) — Skip on a Type pending (`conflicting_names`) row in this group. */
  readonly onSkip: ((doc: PlannedDocument) => void) | undefined;
}) {
  /* CLM-122 (R-122-4) — a conflicting row stays here, and this group's Select all reaches it (D-67-2 as amended). */
  const ticked = rows.filter((doc) => selected.has(rowKey(doc))).length;
  return (
    <>
      <tr className="grp" data-party={party_key}>
        <th scope="rowgroup" colSpan={7}>
          <span className="fl">
            <code>{party_key}</code>
            <SelectionToolbar
              size="sm"
              label={`Approve ${party_key}`}
              noun="documents"
              scope={party_key}
              count={ticked}
              total={rows.length}
              disabled={disabled}
              onSelectAll={() => onSelectGroup(rows, true)}
              onClear={() => onSelectGroup(rows, false)}
            />
          </span>
        </th>
      </tr>
      {rows.map((doc) => (
        <PlannedRow
          key={rowKey(doc)}
          doc={doc}
          checked={selected.has(rowKey(doc))}
          disabled={disabled}
          onToggle={onToggle}
          tags={tagsOf(rowKey(doc))}
          onSetTags={onSetTags === undefined ? undefined : (tags) => onSetTags(rowKey(doc), tags)}
          onSkip={onSkip}
        />
      ))}
    </>
  );
}

/**
 * CLM-122 (R-122-4, R-122-5, R-122-15) — the rows whose name gives no type, in one
 * group after the party groups. Never pre-ticked, and Select all is off here (and
 * the top-level one skips them): the reviewer ticks each one, because a purchase
 * order or a timesheet is exactly such a file. The toolbar stays, for the counter
 * and Clear (D-69-1's vocabulary). The client is the one thing confirmed.
 */
function NoNameTypeGroup({
  rows,
  selected,
  disabled,
  onToggle,
  onSelect,
  tagsOf,
  onSetTags,
  onSkip,
}: {
  readonly rows: readonly PlannedDocument[];
  readonly selected: ReadonlySet<string>;
  readonly disabled: boolean;
  readonly onToggle: (doc: PlannedDocument, checked: boolean) => void;
  readonly onSelect: (keys: readonly string[], on: boolean) => void;
  readonly tagsOf: (key: string) => readonly string[];
  readonly onSetTags: ((key: string, tags: readonly string[]) => void) | undefined;
  readonly onSkip: ((doc: PlannedDocument) => void) | undefined;
}) {
  const keys = rows.map(rowKey);
  const ticked = keys.filter((key) => selected.has(key)).length;
  return (
    <>
      <tr className="grp" data-party="no-name-type">
        <th scope="rowgroup" colSpan={7}>
          <span className="fl">
            <span className="tag">No type in the name — tick each one</span>
            <SelectionToolbar
              size="sm"
              label="Approve no type in the name"
              noun="documents"
              scope="no type in the name"
              count={ticked}
              total={rows.length}
              disabled={disabled}
              selectAllDisabled
              onSelectAll={() => undefined}
              onClear={() => onSelect(keys, false)}
            />
          </span>
          <p className="mut" data-testid="ingest-type-pending-copy">
            {TYPE_PENDING_COPY} Select all is off here: tick each one. Check the client — it is the only thing you
            confirm here.
          </p>
        </th>
      </tr>
      {rows.map((doc) => (
        <PlannedRow
          key={rowKey(doc)}
          doc={doc}
          checked={selected.has(rowKey(doc))}
          disabled={disabled}
          onToggle={onToggle}
          tags={tagsOf(rowKey(doc))}
          onSetTags={onSetTags === undefined ? undefined : (tags) => onSetTags(rowKey(doc), tags)}
          onSkip={onSkip}
          confirmParty
        />
      ))}
    </>
  );
}

/**
 * CLM-77 — one row's tags: chips, and an inline editor for the admin. The
 * editor is a `TagInput` in the cell; the replacement set goes to the store on
 * every change, so a re-render of the group keeps it.
 */
function TagsCell({
  id,
  fileName,
  tags,
  disabled,
  onSetTags,
}: {
  readonly id: string;
  readonly fileName: string;
  readonly tags: readonly string[];
  readonly disabled: boolean;
  readonly onSetTags: ((tags: readonly string[]) => void) | undefined;
}) {
  if (onSetTags === undefined) {
    return (
      <td>
        {tags.length === 0 ? (
          <span className="mut">—</span>
        ) : (
          <ul className="tags-ro" aria-label={`Tags of ${fileName}`}>
            {tags.map((tag) => (
              <li key={tag} className="tag">
                {tag}
              </li>
            ))}
          </ul>
        )}
      </td>
    );
  }
  return (
    <td className="tags-cell">
      <TagInput id={id} label={`Tags of ${fileName}`} value={tags} onChange={onSetTags} disabled={disabled} />
    </td>
  );
}

function PlannedRow({
  doc,
  checked,
  disabled,
  onToggle,
  tags = [],
  onSetTags,
  onSkip,
  confirmParty = false,
}: {
  readonly doc: PlannedDocument;
  readonly checked: boolean;
  readonly disabled: boolean;
  readonly onToggle: (doc: PlannedDocument, checked: boolean) => void;
  /** CLM-77 */
  readonly tags?: readonly string[];
  readonly onSetTags?: ((tags: readonly string[]) => void) | undefined;
  /** CLM-122 (R-122-6) — Skip, offered on a Type pending row only. */
  readonly onSkip?: ((doc: PlannedDocument) => void) | undefined;
  /** CLM-122 (R-122-5) — the client is the thing being confirmed: shown first-class. */
  readonly confirmParty?: boolean;
}) {
  const id = `approve-${doc.item_id}`;
  const key = rowKey(doc);
  /* CLM-76, CLM-118 — the type the row is filed as: the planner's, from the name; nobody picks one (R-118-18). */
  const type = doc.contract_type;
  /* CLM-122 — filed as `OTHER` / `pending` for the AI to type after filing; no type and no number are asked for. */
  const typePending = doc.type_pending_reason !== null;
  /* A SOW the planner gave no number is not executable from here (D-71-0). */
  const needsSowNumber = type === 'SOW' && doc.sow_number === null;
  return (
    <tr className={doc.signed_marker ? undefined : 'unsigned'} data-row={key}>
      <td className="tick">
        <input
          id={id}
          type="checkbox"
          checked={checked}
          disabled={disabled || needsSowNumber}
          aria-label={`Approve ${doc.file_name}`}
          onChange={(event) => onToggle(doc, event.target.checked)}
        />
      </td>
      <FileCell file_name={doc.file_name} web_url={doc.web_url} folder_path={doc.folder_path} />
      <td>
        {typePending ? <ContractTypeTag type={type} confirmation="pending" /> : <code>{type}</code>}
        <span className="fl tagrow">
          {doc.type_pending_reason === 'no_name_type' ? (
            <span className="tag out" title={TYPE_PENDING_COPY} data-testid={`type-no-name-${key}`}>
              no type in the name
            </span>
          ) : doc.contract_type_confidence === 'conflicting' ? (
            <span className="tag bad" title={CONFLICTING_NAME_CAVEAT} data-testid={`type-conflicting-${key}`}>
              conflicting names
            </span>
          ) : doc.contract_type_confidence === 'inferred' ? (
            <span className="tag ai" title="type inferred from SOW number — no MSA / NDA / SOW token in the name or folder">
              inferred
            </span>
          ) : null}
          {doc.amendment_signal_name === null ? null : (
            <span
              className="tag out"
              title={`Possibly an amendment filed on its own: ${AMENDMENT_SIGNAL_COPY[doc.amendment_signal_name]}. An administrator is told; the type is not changed.`}
            >
              possibly an amendment
            </span>
          )}
        </span>
        {doc.contract_type_confidence === 'conflicting' ? (
          <p className="mut" data-testid="ingest-conflicting-caveat">
            {CONFLICTING_NAME_CAVEAT}
          </p>
        ) : null}
      </td>
      <td>
        {typePending ? (
          <span className="mut" data-testid={`type-pending-sow-${key}`}>
            — none; if the AI reads a SOW, an administrator adds its number
          </span>
        ) : type !== 'SOW' ? (
          <span className="mut" title={`An ${type} carries no SOW number (D-76-6).`}>
            —
          </span>
        ) : doc.sow_number === null ? (
          <span className="ferr" role="alert">
            needs a SOW number — the planner found none in the name; re-plan after renaming
          </span>
        ) : (
          <>
            <code>{doc.sow_number}</code>
            <span className="fl tagrow">
              <span className="tag out" title={`Recognised in “${doc.matched_from}”`}>
                {doc.sow_number_source === 'file_name' ? 'from file name' : 'from folder name'}
              </span>
              {doc.sow_number_exact ? null : <span className="tag">prefix match</span>}
              {doc.sow_number_shape === 'dominant_shape' ? null : (
                <span className="tag ai">weak shape: {SOW_SHAPE_LABEL[doc.sow_number_shape]}</span>
              )}
            </span>
          </>
        )}
      </td>
      <td>
        {confirmParty ? (
          /* CLM-122 (R-122-5) — the one thing the reviewer confirms on this row. */
          <strong data-testid={`confirm-party-${key}`} title="Check the client — it is the only thing you confirm here">
            <code>{doc.party_key}</code>
          </strong>
        ) : (
          <code>{doc.party_key}</code>
        )}
        {/* CLM-88 (D-88-5) — the file name's SOW-number prefix named the client, not a folder. */}
        {doc.party_key_source === 'sow_prefix' ? (
          <span className="fl tagrow">
            <span className="tag ai" title={CAVEAT_PARTY_FROM_PREFIX}>
              client from SOW prefix
            </span>
          </span>
        ) : null}
      </td>
      <td>
        <span className="fl">
          {doc.disposition === 'manual_entry' ? (
            <span
              className="tag ai"
              title="Processing-restricted: stored, converted in-tenant, indexed and searchable — never sent to an LLM. Facts are entered by hand."
            >
              manual entry — no LLM
            </span>
          ) : (
            <span className="tag acc">extract</span>
          )}
          {doc.ingest_state === 'changed' ? (
            <span
              className="tag bad"
              title="A previous run holds this item under a different eTag — the document was re-signed or replaced."
            >
              changed since last run
            </span>
          ) : null}
          {doc.existing_contract_key ? (
            <span className="tag out">exists: {doc.existing_contract_key}</span>
          ) : null}
          {doc.plan_state === 'sow_number_unregistered' ? (
            <span className="tag bad" title={doc.caveat ?? ''}>
              unregistered SOW number
            </span>
          ) : null}
          {doc.signed_marker ? null : (
            <span className="tag bad unsigned" title={CAVEAT_UNSIGNED}>
              no signed marker
            </span>
          )}
          {typePending && doc.processing_restricted ? (
            <span className="mut">{TYPE_PENDING_RESTRICTED_COPY}</span>
          ) : null}
          {/* CLM-122 (R-122-6) — a Type pending row can be skipped like a skip row, so it stops coming back. */}
          {typePending && onSkip !== undefined ? (
            <button
              type="button"
              className="btn sm qt"
              disabled={disabled}
              aria-label={`Skip ${doc.file_name}`}
              onClick={() => onSkip(doc)}
            >
              Skip
            </button>
          ) : null}
        </span>
      </td>
      {/* CLM-77 — appended after the pipeline cell. */}
      <TagsCell id={`tags-${doc.item_id}`} fileName={doc.file_name} tags={tags} disabled={disabled} onSetTags={onSetTags} />
    </tr>
  );
}

/* -------------------------------------------------------------------------- */
/* Corrected by hand — CLM-71                                                  */
/* -------------------------------------------------------------------------- */

/**
 * The group of `to_skip` rows the admin corrected. One group, not per party:
 * the party may be the correction itself, and what these rows share is that a
 * human supplied part of the answer. Never pre-ticked; the group's Select all
 * is the explicit act, as for unsigned rows.
 */
function CorrectedGroup({
  entries,
  selected,
  disabled,
  onSelect,
  onRemove,
  tagsOf,
  onSetTags,
}: {
  readonly entries: readonly CorrectedRow[];
  readonly selected: ReadonlySet<string>;
  readonly disabled: boolean;
  readonly onSelect: (keys: readonly string[], on: boolean) => void;
  readonly onRemove: ((key: string) => void) | undefined;
  readonly tagsOf: (key: string) => readonly string[];
  readonly onSetTags: ((key: string, tags: readonly string[]) => void) | undefined;
}) {
  const keys = entries.map((entry) => rowKey(entry.row));
  const ticked = keys.filter((key) => selected.has(key)).length;
  /* CLM-122 (R-122-4, R-122-15) — a correction that files as `no_name_type` is ticked by hand only. */
  const selectAllKeys = entries.filter((entry) => !isNoNameTypeCorrection(entry)).map((entry) => rowKey(entry.row));
  return (
    <>
      <tr className="grp" data-party="corrected">
        <th scope="rowgroup" colSpan={7}>
          <span className="fl">
            <span className="tag manual" title={CAVEAT_MANUAL}>
              Corrected by hand
            </span>
            <SelectionToolbar
              size="sm"
              label="Approve corrected"
              noun="documents"
              scope="corrected by hand"
              count={ticked}
              total={entries.length}
              disabled={disabled}
              selectAllDisabled={selectAllKeys.every((key) => selected.has(key))}
              onSelectAll={() => onSelect(selectAllKeys, true)}
              onClear={() => onSelect(keys, false)}
            />
          </span>
        </th>
      </tr>
      {entries.map((entry) => (
        <CorrectedRowView
          key={rowKey(entry.row)}
          entry={entry}
          checked={selected.has(rowKey(entry.row))}
          disabled={disabled}
          onToggle={(checked) => onSelect([rowKey(entry.row)], checked)}
          onRemove={onRemove}
          tags={tagsOf(rowKey(entry.row))}
          onSetTags={onSetTags}
        />
      ))}
    </>
  );
}

function CorrectedRowView({
  entry,
  checked,
  disabled,
  onToggle,
  onRemove,
  tags,
  onSetTags,
}: {
  readonly entry: CorrectedRow;
  readonly checked: boolean;
  readonly disabled: boolean;
  readonly onToggle: (checked: boolean) => void;
  readonly onRemove: ((key: string) => void) | undefined;
  /** CLM-77 */
  readonly tags: readonly string[];
  readonly onSetTags: ((key: string, tags: readonly string[]) => void) | undefined;
}) {
  const { row, override } = entry;
  const key = rowKey(row);
  const shape = override.display.sow_number_shape;
  /* CLM-118 — the planner's word, else SOW from the typed number; the server files it (or refuses it, naming the rule). */
  const type = override.display.contract_type ?? row.derived_type ?? null;
  /* CLM-122 (R-122-7) — the correction files the row as Type pending: no type, no number. */
  const typePendingReason = override.display.type_pending_reason;
  return (
    <tr className="corrected" data-row={key}>
      <td className="tick">
        <input
          id={`approve-${row.item_id}`}
          type="checkbox"
          checked={checked}
          disabled={disabled}
          aria-label={`Approve ${row.file_name}`}
          onChange={(event) => onToggle(event.target.checked)}
        />
      </td>
      <FileCell file_name={row.file_name} web_url={row.web_url} folder_path={row.folder_path} />
      <td>
        {typePendingReason !== undefined ? (
          <>
            <ContractTypeTag type="OTHER" confirmation="pending" />
            <span className="fl tagrow">
              {typePendingReason === 'no_name_type' ? (
                <span className="tag out" title={TYPE_PENDING_COPY}>
                  no type in the name
                </span>
              ) : (
                <span className="tag bad" title={CONFLICTING_NAME_CAVEAT}>
                  conflicting names
                </span>
              )}
            </span>
          </>
        ) : type === null ? (
          <span className="mut">—</span>
        ) : (
          <code>{type}</code>
        )}
        {typePendingReason === undefined && row.derived_type === null && type !== null ? (
          <span className="fl tagrow">
            <span className="tag out" title="No type word in the name: the number you typed files it as a SOW, never a trusted file-name type.">
              from your number
            </span>
          </span>
        ) : null}
      </td>
      <td>
        {typePendingReason !== undefined ? (
          <span className="mut">— none</span>
        ) : type !== null && type !== 'SOW' ? (
          <span className="mut">—</span>
        ) : override.sow_number_override === undefined ? (
          <span className="mut">from the file or folder name (resolved on execute)</span>
        ) : (
          <code>{override.sow_number_override}</code>
        )}
        <span className="fl tagrow">
          <span className="tag manual" title={CAVEAT_MANUAL}>
            manual
          </span>
          {override.sow_number_override !== undefined ? <span className="tag out">typed</span> : null}
          {shape !== null && shape !== 'dominant_shape' ? (
            <span className="tag ai">weak shape: {SOW_SHAPE_LABEL[shape]}</span>
          ) : null}
        </span>
      </td>
      <td>
        {override.party_key_override === undefined ? (
          <span className="mut">from the folder</span>
        ) : (
          <>
            <code>{override.party_key_override}</code>
            {override.display.party_label ? <span className="path">{override.display.party_label}</span> : null}
          </>
        )}
      </td>
      <td>
        <span className="fl">
          {override.display.processing_restricted ? (
            <span
              className="tag ai"
              title="Processing-restricted: stored, converted in-tenant, indexed and searchable — never sent to an LLM. Facts are entered by hand."
            >
              manual entry — no LLM
            </span>
          ) : (
            <span className="tag acc">extract</span>
          )}
          {/* CLM-122 — as on a planned row: a restricted Type pending correction is never read by the AI (rule 4). */}
          {typePendingReason !== undefined && override.display.processing_restricted ? (
            <span className="mut">{TYPE_PENDING_RESTRICTED_COPY}</span>
          ) : null}
          {onRemove === undefined ? null : (
            <button type="button" className="btn sm qt" disabled={disabled} onClick={() => onRemove(key)}>
              Remove correction
            </button>
          )}
        </span>
      </td>
      <TagsCell
        id={`tags-${row.item_id}`}
        fileName={row.file_name}
        tags={tags}
        disabled={disabled}
        onSetTags={onSetTags === undefined ? undefined : (next) => onSetTags(key, next)}
      />
    </tr>
  );
}

function FileCell({
  file_name,
  web_url,
  folder_path,
}: {
  readonly file_name: string;
  readonly web_url: string | null;
  readonly folder_path: readonly string[];
}) {
  return (
    <td>
      {web_url ? (
        <a href={web_url} target="_blank" rel="noreferrer">
          {file_name}
        </a>
      ) : (
        file_name
      )}
      {folder_path.length > 0 ? <span className="path">{folder_path.join(' / ')}</span> : null}
    </td>
  );
}

/* -------------------------------------------------------------------------- */
/* The receipt                                                                 */
/* -------------------------------------------------------------------------- */

export interface ReceiptCardProps {
  readonly receipt: ExecuteSharepointIngestResponse;
  /** The triples that were posted, so a skipped row's eTag can be compared. */
  readonly approved: readonly ApprovedIngestItem[];
  /** The plan the approval came from — file names for the skipped/failed rows. */
  readonly planned: readonly PlannedDocument[];
  /** CLM-71 — the plan's `to_skip`, so a corrected row's receipt line has its file name too. */
  readonly skippedPlan?: readonly SkippedDocument[];
  /**
   * Re-plan (A4). The planner swaps this card for its loading state the moment
   * the re-plan starts, so the card never renders a busy or disabled state.
   */
  readonly onReplan: () => void;
  /** Start over (D-67-6): clear the kept receipt, plan and form. */
  readonly onStartOver: () => void;
}

export function ReceiptCard({ receipt, approved, planned, skippedPlan = [], onReplan, onStartOver }: ReceiptCardProps) {
  const approvedEtag = new Map(approved.map((item) => [rowKey(item), item.etag]));
  const fileName = new Map([
    ...skippedPlan.map((doc) => [rowKey(doc), doc.file_name] as const),
    ...planned.map((doc) => [rowKey(doc), doc.file_name] as const),
  ]);
  const nameOf = (row: { readonly drive_id: string; readonly item_id: string }) =>
    fileName.get(rowKey(row)) ?? row.item_id;

  return (
    <div className="card receipt" role="status" aria-label="Execute receipt">
      <h3>Executed — receipt</h3>
      <p>
        The API re-crawled the folder, re-planned it and wrote only the approved rows the fresh plan
        still wanted, each as a contract awaiting extraction (or manual entry). Skipped and failed
        rows were not written. To see the folder as it stands now, re-plan it — the ingested rows
        will appear under <em>Already ingested</em>.
      </p>

      <div className="stats">
        <Stat label="Approved" value={receipt.totals.approved} />
        <Stat label="Ingested" value={receipt.totals.ingested} />
        <Stat label="Skipped" value={receipt.totals.skipped} />
        <Stat label="Failed" value={receipt.totals.failed} />
        {/* CLM-122 (R-122-13) — filed with no type, for the AI to read. */}
        <Stat label="Type pending" value={receipt.totals.type_pending} />
      </div>

      <div className="selbar">
        <button type="button" className="btn pri" onClick={onReplan}>
          Re-plan this folder
        </button>
        <button type="button" className="btn sm qt" onClick={onStartOver}>
          Start over
        </button>
        <span className="mut">
          This receipt stays here while you visit other pages; Start over, a browser refresh or
          sign-out clears it.
        </span>
      </div>

      {receipt.ingested.length > 0 ? (
        <section className="skipgrp">
          <h4>
            Ingested <span className="mut">— {receipt.ingested.length.toLocaleString()}</span>
          </h4>
          <div className="tblwrap">
            <table className="tbl">
              <thead>
                <tr>
                  <th scope="col">File</th>
                  <th scope="col">Contract key</th>
                  <th scope="col">Type</th>
                  <th scope="col">Party</th>
                  <th scope="col">SOW number</th>
                  <th scope="col">Status</th>
                </tr>
              </thead>
              <tbody>
                {receipt.ingested.map((row) => (
                  <IngestedRow key={rowKey(row)} row={row} fileName={nameOf(row)} />
                ))}
              </tbody>
            </table>
          </div>
        </section>
      ) : null}

      {receipt.skipped.length > 0 ? (
        <section className="skipgrp">
          <h4>
            Skipped <span className="mut">— {receipt.skipped.length.toLocaleString()}</span>
          </h4>
          <div className="tblwrap">
            <table className="tbl">
              <thead>
                <tr>
                  <th scope="col">File</th>
                  <th scope="col">Why</th>
                </tr>
              </thead>
              <tbody>
                {receipt.skipped.map((row) => {
                  const etagChanged = row.etag !== approvedEtag.get(rowKey(row));
                  return (
                    <tr key={rowKey(row)} data-etag-changed={etagChanged ? 'true' : undefined}>
                      <td>{nameOf(row)}</td>
                      <td>
                        {SKIP_REASON_LABEL[row.reason]}
                        {etagChanged ? (
                          <span className="mut"> — changed since the plan — re-plan</span>
                        ) : null}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </section>
      ) : null}

      {receipt.failed.length > 0 ? (
        <section className="skipgrp">
          <h4>
            Failed <span className="mut">— {receipt.failed.length.toLocaleString()}</span>
          </h4>
          <p className="why">
            Nothing was written for these rows. Fix the cause named in the detail, re-plan, and
            tick them again — there is no partial re-execute.
          </p>
          <div className="tblwrap">
            <table className="tbl">
              <thead>
                <tr>
                  <th scope="col">File</th>
                  <th scope="col">Reason</th>
                  <th scope="col">Detail</th>
                </tr>
              </thead>
              <tbody>
                {receipt.failed.map((row) => (
                  <tr key={rowKey(row)}>
                    <td>{nameOf(row)}</td>
                    <td>
                      <span className="tag bad">{FAILURE_REASON_LABEL[row.reason]}</span>
                    </td>
                    <td>{row.detail}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      ) : null}

      <p className="mut">
        This run was audited (NFR-07): audit <code>{receipt.audit.audit_id}</code>, correlation{' '}
        <code>{receipt.audit.correlation_id}</code>, at {receipt.audit.occurred_at_utc}. Keep the
        correlation id with the Part D log — the receipt is the evidence for{' '}
        <em>unregistered SOW number</em>, <em>no signed marker</em>, <em>manual</em> and <em>Type pending</em> rows.
      </p>
    </div>
  );
}

function IngestedRow({ row, fileName }: { readonly row: IngestedDocument; readonly fileName: string }) {
  return (
    <tr>
      <td>{fileName}</td>
      <td>
        <code>{row.contract_key}</code>
        {/* CLM-112 (R-112-16) — bulk ingest files no amendments (every row is its own root), so the type alone decides. */}
        <BaseDeviationOffer contractKey={row.contract_key} contractType={row.contract_type} isAmendment={false} />
      </td>
      <td>
        {/* CLM-110 (R-110-23) — provisional at intake, or (CLM-122) Type pending: the AI types it after filing. */}
        <ContractTypeTag type={row.contract_type} confirmation={row.contract_type_confirmation} />
      </td>
      <td>
        <code>{row.party_key}</code>
      </td>
      <td>{row.sow_number === null ? <span className="mut">—</span> : <code>{row.sow_number}</code>}</td>
      <td>
        <span className="fl">
          <span className={row.processing_status === 'PendingExtraction' ? 'tag acc' : 'tag ai'}>
            {row.processing_status}
          </span>
          {row.plan_state === 'sow_number_unregistered' ? (
            <span className="tag bad">unregistered SOW number</span>
          ) : null}
          {row.signed_marker ? null : (
            <span className="tag bad unsigned" title={CAVEAT_UNSIGNED}>
              no signed marker
            </span>
          )}
          {row.plan_state === 'sow_number_manual' || row.party_key_source === 'manual' ? (
            /* CLM-71 (D-71-1, D-71-3): the receipt is the evidence — say what was set by hand (never a type, CLM-118). */
            <span className="tag manual" title={CAVEAT_MANUAL}>
              manual
              {row.sow_number_override !== null ? ` — typed: ${row.sow_number_override}` : ''}
              {row.party_key_override !== null ? ` — client set to ${row.party_key_override}` : ''}
            </span>
          ) : null}
          {row.contract_type_name_evidence === 'conflicting' ? (
            <span className="tag bad" title={CONFLICTING_NAME_CAVEAT}>
              conflicting names
            </span>
          ) : null}
          {/* CLM-122 — compared with `pending` only: a Type pending row whose name gave no type. */}
          {row.contract_type_confirmation === 'pending' && row.contract_type_name_evidence === 'untyped' ? (
            <span className="tag out" title={TYPE_PENDING_COPY}>
              no type in the name
            </span>
          ) : null}
        </span>
      </td>
    </tr>
  );
}

function Stat({ label, value }: { readonly label: string; readonly value: number }) {
  return (
    <div className="stat">
      <span>{label}</span>
      <b>{value.toLocaleString()}</b>
    </div>
  );
}
